import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Send, ArrowLeft, CheckCircle, FileText, Receipt, Handshake, Clock, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

const REST_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

interface ReviewData {
  _id: string;
  serviceType: string;
  serviceLabel: string;
  serviceRole: string;
  status: string;
  triggeredAt: string;
  associateSnapshot: {
    nominativo: string;
    ragioneSociale: string;
  };
}

const serviceConfig: Record<string, {
  label: string;
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
  bgPattern: string;
  description: string;
  roleLabel: Record<string, string>;
}> = {
  'svincolo-atti': {
    label: 'Svincolo Atti',
    icon: <FileText className="w-5 h-5" />,
    gradient: 'from-violet-600 via-purple-600 to-indigo-700',
    accentColor: 'violet',
    bgPattern: 'radial-gradient(circle at 20% 80%, rgba(139,92,246,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(99,102,241,0.06) 0%, transparent 50%)',
    description: 'Pratica di svincolo atti completata',
    roleLabel: { requester: 'Come richiedente' }
  },
  'voucher-rimborso': {
    label: 'Voucher Rimborso',
    icon: <Receipt className="w-5 h-5" />,
    gradient: 'from-emerald-600 via-teal-600 to-cyan-700',
    accentColor: 'emerald',
    bgPattern: 'radial-gradient(circle at 80% 80%, rgba(16,185,129,0.08) 0%, transparent 50%), radial-gradient(circle at 20% 20%, rgba(6,182,212,0.06) 0%, transparent 50%)',
    description: 'Scambio voucher rimborso completato',
    roleLabel: { owner: 'Come proprietario', booker: 'Come prenotante' }
  },
  'collabora': {
    label: 'Collabora',
    icon: <Handshake className="w-5 h-5" />,
    gradient: 'from-amber-500 via-orange-500 to-rose-600',
    accentColor: 'amber',
    bgPattern: 'radial-gradient(circle at 30% 70%, rgba(245,158,11,0.08) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(244,63,94,0.06) 0%, transparent 50%)',
    description: 'Collaborazione con un collega',
    roleLabel: { requester: 'Come richiedente' }
  }
};

const ratingConfig = [
  { label: 'Scarso', emoji: '😔', color: 'text-red-500' },
  { label: 'Sufficiente', emoji: '😐', color: 'text-orange-500' },
  { label: 'Buono', emoji: '🙂', color: 'text-yellow-500' },
  { label: 'Ottimo', emoji: '😊', color: 'text-lime-500' },
  { label: 'Eccellente', emoji: '🤩', color: 'text-green-500' }
];

const ServiceReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [review, setReview] = useState<ReviewData | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchReview();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [id]);

  const fetchReview = async () => {
    try {
      const token = localStorage.getItem('associate_token');
      const { data } = await axios.get(`${REST_BASE}/service-reviews/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setReview(data.review);
        setAvailableTags(data.availableTags || []);
        if (data.review.status !== 'pending') {
          setSubmitted(true);
        }
      }
    } catch {
      toast.error('Recensione non trovata');
      navigate('/area-riservata');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Seleziona una valutazione');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('associate_token');
      const { data } = await axios.post(
        `${REST_BASE}/service-reviews/${id}/submit`,
        { rating, comment: comment.trim(), tags: selectedTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setSubmitted(true);
        toast.success('Grazie per la tua recensione!');
      }
    } catch {
      toast.error('Errore nell\'invio della recensione');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const config = review ? serviceConfig[review.serviceType] : null;
  const activeRating = hoveredRating || rating;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-10 h-10">
            <div className="absolute inset-0 rounded-full border-2 border-gray-200 dark:border-gray-700" />
            <div className="absolute inset-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  /* ─── Schermata di conferma ─── */
  if (submitted) {
    return (
      <>
        <style>{`
          @keyframes confettiDrift {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(120px) rotate(720deg); opacity: 0; }
          }
          @keyframes scaleIn {
            0% { transform: scale(0.6); opacity: 0; }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes fadeUp {
            0% { transform: translateY(12px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          .anim-scale-in { animation: scaleIn 0.5s cubic-bezier(.22,.68,0,1.15) both; }
          .anim-fade-up { animation: fadeUp 0.4s ease-out both; }
        `}</style>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="relative max-w-md w-full">
            {/* Confetti decorativi */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-48 h-24 pointer-events-none overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    left: `${12 + i * 12}%`,
                    backgroundColor: ['#fbbf24', '#34d399', '#818cf8', '#f472b6', '#fb923c', '#22d3ee', '#a78bfa', '#f87171'][i],
                    animation: `confettiDrift ${1.5 + i * 0.15}s ease-out ${i * 0.08}s both`
                  }}
                />
              ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
              <div className="anim-scale-in mb-5">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="w-9 h-9 text-green-500" />
                </div>
              </div>
              <h2 className="anim-fade-up text-base font-semibold text-gray-900 dark:text-white mb-2" style={{ animationDelay: '0.15s' }}>
                Grazie per la tua recensione!
              </h2>
              <p className="anim-fade-up text-sm text-gray-500 dark:text-gray-400 mb-8 leading-relaxed" style={{ animationDelay: '0.25s' }}>
                La tua opinione aiuta i colleghi a scegliere i servizi migliori
                e ci permette di migliorare continuamente.
              </p>
              <button
                onClick={() => navigate('/area-riservata')}
                className="anim-fade-up inline-flex items-center gap-2 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 text-sm font-medium transition-colors"
                style={{ animationDelay: '0.35s' }}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Torna alla dashboard
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ─── Form recensione ─── */
  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          0% { transform: translateY(16px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes starPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.25); }
          100% { transform: scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .anim-slide-up { animation: fadeSlideUp 0.45s cubic-bezier(.22,.68,0,1.1) both; }
        .star-pop { animation: starPop 0.25s cubic-bezier(.22,.68,0,1.3); }
        .shimmer-btn {
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
      <div
        className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 sm:py-10 px-4"
        style={{ background: config?.bgPattern }}
      >
        <div className="max-w-lg mx-auto">
          {/* Back link */}
          <button
            onClick={() => navigate('/area-riservata')}
            className="anim-slide-up flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla dashboard
          </button>

          {/* Service context header */}
          {config && (
            <div
              className={`anim-slide-up rounded-xl bg-gradient-to-r ${config.gradient} p-4 sm:p-5 mb-4 text-white shadow-lg`}
              style={{ animationDelay: '0.05s' }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center">
                  {config.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold mb-0.5">{config.label}</h2>
                  <p className="text-xs opacity-80">{config.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs opacity-70">
                    {review?.serviceRole && config.roleLabel[review.serviceRole] && (
                      <span className="flex items-center gap-1">
                        {config.roleLabel[review.serviceRole]}
                      </span>
                    )}
                    {review?.triggeredAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(review.triggeredAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main card */}
          <div
            className="anim-slide-up bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
            style={{ animationDelay: '0.1s' }}
          >
            {/* Card header */}
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                <h1 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Com'è andata la tua esperienza?
                </h1>
              </div>
              <p className="text-2xs text-gray-400 dark:text-gray-500 ml-6">
                Bastano pochi secondi — la tua opinione fa la differenza
              </p>
            </div>

            <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-6">
              {/* ─── Stars ─── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Valutazione
                </label>
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isActive = star <= activeRating;
                    const justClicked = star === rating && star === hoveredRating;
                    return (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        onClick={() => setRating(star)}
                        className={`relative p-1.5 rounded-lg transition-all duration-150 ${
                          isActive
                            ? 'bg-yellow-50 dark:bg-yellow-900/20 scale-105'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        } ${justClicked ? 'star-pop' : ''}`}
                      >
                        <Star
                          className={`w-9 h-9 sm:w-10 sm:h-10 transition-all duration-150 ${
                            isActive
                              ? 'text-yellow-400 fill-yellow-400 drop-shadow-sm'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                {activeRating > 0 && (
                  <div className="flex items-center justify-center gap-2 mt-2.5 transition-all">
                    <span className="text-base">{ratingConfig[activeRating - 1].emoji}</span>
                    <span className={`text-sm font-medium ${ratingConfig[activeRating - 1].color}`}>
                      {ratingConfig[activeRating - 1].label}
                    </span>
                  </div>
                )}
              </div>

              {/* ─── Divider ─── */}
              <div className="border-t border-gray-100 dark:border-gray-700" />

              {/* ─── Tags ─── */}
              {availableTags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2.5">
                    Com'è stato il servizio?
                    <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">(opzionale)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all duration-150 border ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 shadow-sm'
                              : 'bg-white dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                        >
                          {isSelected && <span className="mr-1">✓</span>}
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ─── Comment ─── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Racconta la tua esperienza
                  <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">(opzionale)</span>
                </label>
                <div className="relative">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, 500))}
                    placeholder="Cosa ti ha colpito di più? Cosa potremmo migliorare?"
                    rows={4}
                    className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-700/30 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 dark:focus:border-blue-500 resize-none transition-all"
                  />
                  <div className="absolute bottom-2.5 right-3 flex items-center gap-1">
                    <div
                      className="h-1 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden"
                      style={{ width: '48px' }}
                    >
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          comment.length > 450 ? 'bg-orange-400' : comment.length > 0 ? 'bg-blue-400' : ''
                        }`}
                        style={{ width: `${Math.min((comment.length / 500) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-2xs text-gray-400 dark:text-gray-500 tabular-nums">
                      {comment.length}/500
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Submit footer ─── */}
            <div className="px-5 sm:px-6 pb-5 sm:pb-6">
              <button
                onClick={handleSubmit}
                disabled={rating === 0 || submitting}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  rating === 0
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : submitting
                      ? 'bg-blue-500 text-white cursor-wait'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg active:scale-[0.98]'
                }`}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Invia la tua recensione
                  </>
                )}
              </button>
              {rating === 0 && (
                <p className="text-center text-2xs text-gray-400 dark:text-gray-500 mt-2">
                  Seleziona almeno una stella per continuare
                </p>
              )}
            </div>
          </div>

          {/* Privacy note */}
          <p
            className="anim-slide-up text-center text-2xs text-gray-400 dark:text-gray-600 mt-4 px-4"
            style={{ animationDelay: '0.2s' }}
          >
            La tua recensione potrebbe essere pubblicata nella pagina della Rete Certificata dopo approvazione.
          </p>
        </div>
      </div>
    </>
  );
};

export default ServiceReviewPage;
